import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { LuPencil, LuTrash2, LuPlus, LuCheck, LuX } from "react-icons/lu";
import { API, colors } from "../shared/constants";
import { styles, inputStyle } from "../shared/styles";

export function AssetInfoSettings() {
  const [categories, setCategories] = useState([]);
  const [allInfoFields, setAllInfoFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});

  const [addingFor, setAddingFor] = useState(null);
  const [addingName, setAddingName] = useState("");
  const [addingSaving, setAddingSaving] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingSaving, setEditingSaving] = useState(false);

  const addInputRef = useRef(null);
  const editInputRef = useRef(null);

  const loadAll = async () => {
    try {
      const [catRes, fieldRes] = await Promise.all([
        axios.get(`${API}/asset-categories/`),
        axios.get(`${API}/asset-info-fields/`),
      ]);
      setCategories(catRes.data);
      setAllInfoFields(fieldRes.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (addingFor !== null && addInputRef.current) {
      addInputRef.current.focus();
    }
  }, [addingFor]);

  useEffect(() => {
    if (editingId !== null && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  const toggleCollapse = (catId) =>
    setCollapsed((p) => ({ ...p, [catId]: !p[catId] }));

  const handleToggle = async (cat) => {
    const newVal = !cat.allow_asset_info;
    try {
      const res = await axios.patch(`${API}/asset-categories/${cat.id}/`, {
        allow_asset_info: newVal,
      });
      setCategories((prev) =>
        prev.map((c) => (c.id === cat.id ? res.data : c))
      );
    } catch {}
  };

  const startAdding = (categoryId) => {
    setEditingId(null);
    setAddingFor(categoryId);
    setAddingName("");
  };

  const handleInlineAdd = async (categoryId) => {
    if (!addingName.trim()) return;
    setAddingSaving(true);
    try {
      const res = await axios.post(`${API}/asset-info-fields/`, {
        category: categoryId,
        label: addingName.trim(),
        order: 0,
      });
      setAllInfoFields((prev) => [...prev, res.data]);
      setAddingName("");
      addInputRef.current?.focus();
    } catch {}
    setAddingSaving(false);
  };

  const cancelAdding = () => {
    setAddingFor(null);
    setAddingName("");
  };

  const startEditing = (field) => {
    setAddingFor(null);
    setEditingId(field.id);
    setEditingName(field.label);
  };

  const handleInlineEdit = async (fieldId) => {
    if (!editingName.trim()) { setEditingId(null); return; }
    setEditingSaving(true);
    try {
      const res = await axios.patch(`${API}/asset-info-fields/${fieldId}/`, {
        label: editingName.trim(),
      });
      setAllInfoFields((prev) => prev.map((f) => f.id === fieldId ? res.data : f));
      setEditingId(null);
    } catch {}
    setEditingSaving(false);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName("");
  };

  const handleDelete = async (field) => {
    try {
      await axios.patch(`${API}/asset-info-fields/${field.id}/`, { is_active: false });
      setAllInfoFields((prev) => prev.filter((f) => f.id !== field.id));
    } catch {}
  };

  return (
    <div>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Asset Info Fields</div>
        <div style={{ fontSize: "13px", color: colors.slate }}>
          Define custom info fields per EFSM — toggle on categories where techs need internal asset notes
        </div>
      </div>
      <div style={styles.content}>
        <div style={styles.card}>
          {loading ? (
            <div style={styles.loading}>Loading...</div>
          ) : (
            categories.map((cat) => {
              const fields = allInfoFields.filter((f) => f.category === cat.id);
              const isCollapsed = collapsed[cat.id];
              const isAddingHere = addingFor === cat.id;
              const isOn = cat.allow_asset_info;
              return (
                <div key={cat.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "10px 20px",
                      backgroundColor: colors.darkGrey,
                      cursor: "pointer",
                    }}
                    onClick={() => toggleCollapse(cat.id)}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <span style={{ color: colors.white, fontWeight: "600", fontSize: "13px" }}>
                        {cat.name}
                      </span>
                      {isOn && fields.length > 0 && (
                        <span style={{
                          fontSize: "11px",
                          backgroundColor: "rgba(255,255,255,0.15)",
                          color: "rgba(255,255,255,0.85)",
                          padding: "2px 8px",
                          borderRadius: "10px",
                          fontWeight: "600",
                        }}>
                          {fields.length} field{fields.length !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      {/* Toggle button */}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggle(cat); }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          backgroundColor: isOn ? colors.green : "rgba(255,255,255,0.15)",
                          color: "rgba(255,255,255,0.9)",
                          border: isOn ? "1.5px solid rgba(255,255,255,0.3)" : "1.5px solid rgba(255,255,255,0.2)",
                          padding: "3px 12px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "700",
                          cursor: "pointer",
                          minHeight: "auto",
                          transition: "background 0.15s",
                        }}
                      >
                        {isOn ? "ON" : "OFF"}
                      </button>
                      {isOn && (
                        <button
                          onClick={(e) => { e.stopPropagation(); startAdding(cat.id); }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            backgroundColor: "transparent",
                            color: "rgba(255,255,255,0.8)",
                            border: "1.5px solid rgba(255,255,255,0.3)",
                            padding: "3px 10px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "600",
                            cursor: "pointer",
                            minHeight: "auto",
                          }}
                        >
                          <LuPlus size={12} />
                          Add Field
                        </button>
                      )}
                      <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "12px" }}>
                        {isCollapsed ? "▶" : "▼"}
                      </span>
                    </div>
                  </div>

                  {!isCollapsed && isOn && (
                    <div style={{ backgroundColor: colors.white }}>
                      {fields.map((field) => (
                        <div
                          key={field.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "8px 20px 8px 32px",
                            borderBottom: `1px solid ${colors.border}`,
                          }}
                        >
                          {editingId === field.id ? (
                            <div style={{ display: "flex", alignItems: "center", gap: "6px", flex: 1 }}>
                              <input
                                ref={editInputRef}
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") handleInlineEdit(field.id);
                                  if (e.key === "Escape") cancelEditing();
                                }}
                                onBlur={() => handleInlineEdit(field.id)}
                                disabled={editingSaving}
                                style={{
                                  ...inputStyle,
                                  padding: "4px 8px",
                                  fontSize: "13px",
                                  flex: 1,
                                  maxWidth: "320px",
                                }}
                              />
                              <button
                                onMouseDown={(e) => { e.preventDefault(); handleInlineEdit(field.id); }}
                                disabled={editingSaving}
                                style={{
                                  background: "none", border: "none", cursor: "pointer",
                                  color: "#16a34a", padding: "4px", minHeight: "auto",
                                  display: "flex", alignItems: "center",
                                }}
                              >
                                <LuCheck size={14} />
                              </button>
                              <button
                                onMouseDown={(e) => { e.preventDefault(); cancelEditing(); }}
                                style={{
                                  background: "none", border: "none", cursor: "pointer",
                                  color: "#94a3b8", padding: "4px", minHeight: "auto",
                                  display: "flex", alignItems: "center",
                                }}
                              >
                                <LuX size={14} />
                              </button>
                            </div>
                          ) : (
                            <>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={{ color: colors.blue, fontSize: "9px" }}>●</span>
                                <span style={{ fontSize: "13px", color: colors.text }}>{field.label}</span>
                              </div>
                              <div style={{ display: "flex", gap: "2px" }}>
                                <IconBtn onClick={() => startEditing(field)} Icon={LuPencil} hoverColor="#2563eb" />
                                <IconBtn onClick={() => handleDelete(field)} Icon={LuTrash2} hoverColor="#dc2626" />
                              </div>
                            </>
                          )}
                        </div>
                      ))}

                      {isAddingHere ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "8px 20px 8px 32px",
                            backgroundColor: "#f0f9ff",
                            borderBottom: `1px solid ${colors.border}`,
                          }}
                        >
                          <span style={{ color: colors.blue, fontSize: "9px" }}>●</span>
                          <input
                            ref={addInputRef}
                            value={addingName}
                            onChange={(e) => setAddingName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleInlineAdd(cat.id);
                              if (e.key === "Escape") cancelAdding();
                            }}
                            disabled={addingSaving}
                            placeholder="Type field label…"
                            style={{
                              ...inputStyle,
                              padding: "4px 8px",
                              fontSize: "13px",
                              flex: 1,
                              maxWidth: "320px",
                            }}
                          />
                          <button
                            onClick={() => handleInlineAdd(cat.id)}
                            disabled={addingSaving || !addingName.trim()}
                            style={{
                              backgroundColor: colors.blue,
                              color: colors.white,
                              border: "none",
                              padding: "4px 12px",
                              borderRadius: "6px",
                              fontSize: "12px",
                              fontWeight: "600",
                              cursor: "pointer",
                              minHeight: "auto",
                              opacity: addingName.trim() ? 1 : 0.5,
                            }}
                          >
                            {addingSaving ? "Saving…" : "Save"}
                          </button>
                          <button
                            onClick={cancelAdding}
                            style={{
                              background: "none", border: "none", cursor: "pointer",
                              color: "#94a3b8", padding: "4px", minHeight: "auto",
                              display: "flex", alignItems: "center",
                            }}
                          >
                            <LuX size={14} />
                          </button>
                        </div>
                      ) : (
                        fields.length === 0 && (
                          <div style={{
                            padding: "10px 32px",
                            fontSize: "12px",
                            color: "#94a3b8",
                            fontStyle: "italic",
                          }}>
                            No fields yet — click Add Field to create one.
                          </div>
                        )
                      )}
                    </div>
                  )}

                  {!isCollapsed && !isOn && (
                    <div style={{
                      padding: "8px 20px 8px 32px",
                      fontSize: "12px",
                      color: "#94a3b8",
                      fontStyle: "italic",
                      backgroundColor: colors.white,
                    }}>
                      Asset info is off for this category. Toggle ON to add fields.
                    </div>
                  )}
                </div>
              );
            })
          )}
          {!loading && (
            <div style={styles.count}>
              {allInfoFields.length} field{allInfoFields.length !== 1 ? "s" : ""} across{" "}
              {categories.filter((c) => c.allow_asset_info).length} active categor
              {categories.filter((c) => c.allow_asset_info).length !== 1 ? "ies" : "y"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function IconBtn({ onClick, Icon, hoverColor }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "none",
        border: "none",
        padding: "4px",
        borderRadius: "4px",
        cursor: "pointer",
        color: hovered ? hoverColor : "#94a3b8",
        minHeight: "auto",
        display: "flex",
        alignItems: "center",
        transition: "color 0.12s",
      }}
    >
      <Icon size={14} />
    </button>
  );
}
